import React, { ReactNode } from 'react'
import styled from 'styled-components'
import AgreementIcon from '../../atoms/graphics/agreement-icon'
import ArrowRightIcon from '../../atoms/graphics/arrow-right'
import CheckCircleIcon from '../../atoms/graphics/check-circle-icon'
import FlowerIcon from '../../atoms/graphics/flower-icon'
import InvitationIcon from '../../atoms/graphics/invitation-icon'
import RegisterIcon from '../../atoms/graphics/register-icon'
import theme from '../../atoms/theme'

interface Props {
  checkedSteps: number
}

interface StepProps {
  children: ReactNode
  step: number
  processText: string
  backgroundColour: string
  active?: boolean
}

interface StyleProps {
  backgroundColour?: string
  colour?: string
}

const ProcessContainer = styled.div`
  padding: 5rem 3rem 3rem;
  width: 100%;
  display: flex;
  align-items: start;
  justify-content: space-around;
  @media only screen and ${theme.breakpoints.toLargeScreen} {
    flex-direction: column;
    padding: 0;
  }
`

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 16%;
  @media only screen and ${theme.breakpoints.toLargeScreen} {
    width: 100%;
    flex-direction: row;
    padding-bottom: 25px;
    &:last-child {
      padding-bottom: 0;
    }
  }
`

const ArrowContainer = styled.div`
  display: flex;
  height: 20rem;
  align-items: center;
  @media only screen and ${theme.breakpoints.toLargeScreen} {
    display: none;
  }
`

const IconContainer = styled.div`
  height: 16.4rem;
  width: 16.4rem;
  background-color: ${(props: StyleProps): string => props.backgroundColour};
  border-radius: 50%;
  margin-bottom: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  @media only screen and ${theme.breakpoints.toLargeScreen} {
    height: 7.2rem;
    width: 7.2rem;
    margin-bottom: 0;
  }
`

const Icon = styled.div`
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 7rem;
  width: 7rem;
  @media only screen and ${theme.breakpoints.toLargeScreen} {
    height: 4rem;
    width: 4rem;
    margin-bottom: 0;
  }
`

const ProcessText = styled.div`
  font-family: ${theme.fonts.primary};
  display: flex;
  @media only screen and ${theme.breakpoints.toLargeScreen} {
    width: 66%;
    padding-left: 10px;
  }
`

const ProcessStep = styled.div`
  color: ${(props: StyleProps): string => props.colour};
  font-family: ${theme.fonts.primaryBlack};
  padding-right: 0.5rem;
`

const StepIconContainer = styled.div`
  position: relative;
  padding-right: 1rem;
  height: 20rem;
  @media only screen and ${theme.breakpoints.toLargeScreen} {
    height: auto;
    padding-bottom: 1rem;
  }
`

const Check = styled.div`
  position: absolute;
  right: 0;
  bottom: 1.5rem;
  height: 4.2rem;
  width: 4.2rem;
  @media only screen and ${theme.breakpoints.toLargeScreen} {
    height: 2.4rem;
    width: 2.4rem;
    bottom: 0;
  }
`

const Step = (props: StepProps): JSX.Element => {
  return (
    <StepContainer>
      <StepIconContainer>
        <IconContainer backgroundColour={props.backgroundColour}>
          <Icon>{props.children}</Icon>
        </IconContainer>
        {props.active && (
          <Check>
            <CheckCircleIcon colour={theme.colours.success1} />
          </Check>
        )}
      </StepIconContainer>
      <ProcessText>
        <ProcessStep colour={props.backgroundColour}>
          {props.step}.{' '}
        </ProcessStep>
        <div>{props.processText}</div>
      </ProcessText>
    </StepContainer>
  )
}

const RegistrationSteps = (props: Props): JSX.Element => {
  const registrationSteps = [
    {
      text: 'Register your interest to be a Service Provider',
      backgroundColour: theme.colours.secondary,
      icon: <RegisterIcon colour={theme.colours.white} />,
    },
    {
      text: 'Receive invitation to register as a Service Provider',
      backgroundColour: theme.colours.quaternary,
      icon: <InvitationIcon colour={theme.colours.white} />,
    },
    {
      text: 'Cultural competency, business advisory and technical checking',
      backgroundColour: theme.colours.primary,
      icon: <FlowerIcon colour={theme.colours.white} />,
    },
    {
      text: 'Agreement to be a service provider',
      backgroundColour: theme.colours.tertiary,
      icon: <AgreementIcon colour={theme.colours.white} />,
    },
  ]
  return (
    <ProcessContainer>
      {registrationSteps.map((step, index) => {
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
          <React.Fragment key={index}>
            <Step
              step={index + 1}
              backgroundColour={step.backgroundColour}
              processText={step.text}
              active={index < props.checkedSteps}
            >
              {step.icon}
            </Step>
            {index < registrationSteps.length - 1 && (
              <ArrowContainer>
                <ArrowRightIcon colour={theme.colours.black} />
              </ArrowContainer>
            )}
          </React.Fragment>
        )
      })}
    </ProcessContainer>
  )
}

export default RegistrationSteps
